import { NgClass } from '@angular/common';
import {
    afterNextRender,
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    forwardRef,
    inject,
    Injector,
    input,
    model,
    output,
    signal,
    viewChild,
    viewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DEALER_THEME } from '@hyundai/shared-lib';
import { InputSelectWrapperComponent } from '../input-select-wrapper/input-select-wrapper.component';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';

type SelectValue = string | undefined;

@Component({
    standalone: true,
    imports: [NgClass, InputWrapperComponent, InputSelectWrapperComponent],
    selector: 'ui-input-select',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    templateUrl: './m020-c030-input-select.component.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputSelectComponent), multi: true }],
})
export class InputSelectComponent implements ControlValueAccessor {
    formControl = input<FormControl>();

    listbox = viewChildren('listbox', { read: ElementRef });
    wrapper = viewChild('wrapper', { read: ElementRef<HTMLElement> });

    options = input.required<Array<{ value: SelectValue; label: string }>>();
    label = input<string>();
    ariaLabel = input<string>();
    placeholder = input<string>();

    error = input(false, { transform: booleanAttribute });
    errorMsg = input<string>();

    dropdownAsOverlay = input(false, { transform: booleanAttribute });
    wrapValue = input(false, { transform: booleanAttribute });

    theme = input<DEALER_THEME>('light');

    open = signal(false);
    trackSelect = output();

    injector = inject(Injector);

    selected = model<SelectValue>();
    selectedLabel = computed(() => {
        return this.options().find((opt) => opt.value === this.selected());
    });
    selectedObjChanged = output<{ value: SelectValue; label: string } | undefined>();

    disabled = model(false);
    inputDisabled = signal(false);
    withNullSelect = input(false, { transform: booleanAttribute });
    nullSelectCustomLabel = input('Option wählen');

    onChange?: (value: SelectValue) => void;
    onTouch?: () => void;

    writeValue(value: SelectValue) {
        this.selected.set(value);
    }

    registerOnChange(fn: (value: SelectValue) => void) {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void) {
        this.onTouch = fn;
    }

    isDisabled = computed(() => this.disabled() || this.inputDisabled());

    setDisabledState(isDisabled: boolean) {
        this.inputDisabled.set(isDisabled);
    }

    setValue(value: string | undefined) {
        this.selected.set(value);
        this.open.set(false);
        this.onChange?.(value);
        this.trackSelect.emit();
        this.selectedObjChanged.emit(this.selectedLabel());

        queueMicrotask(() => {
            this.wrapper()?.nativeElement.focus();
        });
    }

    openSelect() {
        this.open.set(!this.open());
        this.onTouch?.();

        if (this.open()) {
            afterNextRender(
                () => {
                    const listboxEl = this.listbox()[0]?.nativeElement as HTMLElement | undefined;
                    const firstOption = listboxEl?.querySelector('[role="option"]') as HTMLElement | null;
                    firstOption?.focus();
                },
                { injector: this.injector },
            );
        }
    }
}
